import React, { useState, useRef, useEffect } from 'react'
import { 
  PaperAirplaneIcon, 
  SparklesIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'

function Chatbot() {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: "Hi! I'm your Points FYI AI assistant. I can help you analyze redemptions, calculate CPP, and provide insights about your loyalty program value. Ask me anything like 'Is 80k CX points for LAX-HKG business class a good deal?'",
      timestamp: new Date()
    }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!input.trim() || loading) return

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setLoading(true)

    try {
      // Simulate AI response for now - in production this would call your LLM API
      const response = await simulateAIResponse(input)
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, botMessage])
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setLoading(false)
    }
  }

  const simulateAIResponse = async (userInput) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000))

    const inputLower = userInput.toLowerCase()
    
    // Simple rule-based responses for demo purposes
    if (inputLower.includes('lax') && inputLower.includes('hkg') && inputLower.includes('80k') && inputLower.includes('cx')) {
      return 'Great question! Let me analyze that LAX-HKG business class redemption on Cathay Pacific:\n\n' +
             '✈️ **Route Analysis:**\n' +
             '- LAX-HKG is approximately 7,200 miles\n' +
             '- Business class typically costs $2,500-3,500 cash\n' +
             '- Your 80,000 CX points = **3.1¢ CPP** (assuming $2,500 cash value)\n\n' +
             '🎯 **Verdict: EXCELLENT DEAL!**\n' +
             '- Industry average for business class: 1.5-2.5¢ CPP\n' +
             '- Your redemption is significantly above average\n' +
             '- Cathay Pacific business class is premium quality\n' +
             '- This route often has high cash prices\n\n' +
             '💡 **Pro Tips:**\n' +
             '- Consider booking during peak seasons for even better value\n' +
             '- Check for partner airline availability\n' +
             '- Look for premium economy if you want to stretch points further\n\n' +
             'Would you like me to help you track this redemption in your account?'
    }
    
    if (inputLower.includes('cpp') || inputLower.includes('cents per point')) {
              return '**CPP (Cents Per Point)** is the key metric for evaluating redemption value:\n\n' +
               '💰 **Calculation:** CPP = Cash Price ÷ Points Used × 100\n\n' +
               '📊 **Industry Benchmarks:**\n' +
               '- **Excellent:** 2.5¢+ CPP\n' +
               '- **Good:** 1.5-2.4¢ CPP\n' +
               '- **Fair:** 1.0-1.4¢ CPP\n' +
               '- **Poor:** <1.0¢ CPP\n\n' +
               '🎯 **High-Value Redemptions:**\n' +
               '- International business/first class flights\n' +
               '- Peak season travel\n' +
               '- Premium hotel stays\n' +
               '- Car rentals during high-demand periods\n\n' +
               '💡 **Pro Tip:** Always compare CPP to the cash price you\'d actually pay, not the inflated "retail" price.'
    }

    if (inputLower.includes('hotel') || inputLower.includes('marriott') || inputLower.includes('hilton')) {
              return '**Hotel Redemption Tips:**\n\n' +
               '🏨 **Best Value Scenarios:**\n' +
               '- Luxury properties in expensive cities\n' +
               '- Peak season bookings\n' +
               '- Suite upgrades\n' +
               '- Resort destinations\n\n' +
               '📊 **Hotel CPP Benchmarks:**\n' +
               '- **Luxury:** 0.8-1.2¢ CPP\n' +
               '- **Upper Upscale:** 0.6-0.9¢ CPP\n' +
               '- **Upscale:** 0.4-0.7¢ CPP\n' +
               '- **Mid-scale:** 0.3-0.5¢ CPP\n\n' +
               '💡 **Pro Strategies:**\n' +
               '- Use points for expensive nights, cash for cheaper ones\n' +
               '- Combine with elite status benefits\n' +
               '- Look for "Points + Cash" options\n' +
               '- Consider transfer partners for better value\n\n' +
               'Would you like me to analyze a specific hotel redemption?'
    }

    if (inputLower.includes('transfer') || inputLower.includes('partner')) {
              return '**Transfer Partner Strategies:**\n\n' +
               '🔄 **When to Transfer:**\n' +
               '- Promotional bonuses (20-50% bonus)\n' +
               '- High-value redemption opportunities\n' +
               '- Limited availability on primary program\n' +
               '- Better award charts\n\n' +
               '📊 **Top Transfer Partners:**\n' +
               '- **Amex MR → Aeroplan:** Great for Star Alliance\n' +
               '- **Chase UR → Hyatt:** Premium hotel value\n' +
               '- **Citi TYP → Turkish:** Sweet spot redemptions\n' +
               '- **Capital One → Turkish:** Good for international flights\n\n' +
               '⚠️ **Transfer Cautions:**\n' +
               '- Transfers are usually one-way\n' +
               '- Wait for bonuses when possible\n' +
               '- Research award availability first\n' +
               '- Consider transfer ratios carefully\n\n' +
               'Need help with a specific transfer scenario?'
    }

    // Default response
    return 'I understand you\'re asking about "' + userInput + '". Let me help you analyze this:\n\n' +
           '🔍 **To provide the best analysis, I need to know:**\n' +
           '- What type of redemption (flight/hotel/car)?\n' +
           '- Which loyalty program?\n' +
           '- Points required vs. cash price?\n' +
           '- Travel dates and class of service?\n\n' +
           '💡 **Pro Tip:** The more specific you are, the better I can help you evaluate the value!\n\n' +
           'You can also ask me about:\n' +
           '- CPP calculations and benchmarks\n' +
           '- Transfer partner strategies\n' +
           '- Hotel vs. flight value\n' +
           '- Seasonal redemption tips\n\n' +
           'What would you like to know more about?'
  }

  const quickQuestions = [
    "Is 80k CX points for LAX-HKG business class a good deal?",
    "How do I calculate CPP?",
    "What are the best hotel redemption strategies?",
    "When should I transfer points between programs?"
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center">
            <SparklesIcon className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">AI Chatbot</h1>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Get instant insights about your loyalty program redemptions. Ask me anything about CPP, 
          transfer strategies, or whether a redemption is worth it!
        </p>
      </div>

      {/* Quick Questions */}
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {quickQuestions.map((question, index) => (
            <button
              key={index}
              onClick={() => setInput(question)}
              className="p-4 bg-gradient-to-r from-primary-50 to-purple-50 border border-primary-200 rounded-lg text-left hover:from-primary-100 hover:to-purple-100 transition-colors"
            >
              <div className="flex items-start space-x-3">
                <LightBulbIcon className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{question}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Chat Interface */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-primary-500 to-purple-600 px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Points FYI AI Assistant</h3>
                <p className="text-primary-100 text-sm">Ask me about redemptions, CPP, and loyalty programs</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-96 overflow-y-auto p-6 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                                  className={'flex ' + (message.type === 'user' ? 'justify-end' : 'justify-start')}
              >
                                  <div className={'flex items-start space-x-3 max-w-xs lg:max-w-md ' + (
                    message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  )}>
                                      <div className={'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ' + (
                      message.type === 'user' 
                        ? 'bg-primary-600' 
                        : 'bg-gradient-to-br from-primary-500 to-purple-600'
                    )}>
                    {message.type === 'user' ? (
                      <UserIcon className="w-4 h-4 text-white" />
                    ) : (
                      <SparklesIcon className="w-4 h-4 text-white" />
                    )}
                  </div>
                                      <div className={'rounded-2xl px-4 py-3 ' + (
                      message.type === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    )}>
                    <div className="whitespace-pre-wrap text-sm">{message.content}</div>
                                          <div className={'text-xs mt-2 ' + (
                        message.type === 'user' ? 'text-primary-100' : 'text-gray-500'
                      )}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-purple-600 rounded-full flex items-center justify-center">
                    <SparklesIcon className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-gray-100 rounded-2xl px-4 py-3">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4">
            <form onSubmit={handleSubmit} className="flex space-x-4">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me about your redemptions, CPP, or loyalty programs..."
                className="flex-1 input-field"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <CurrencyDollarIcon className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">CPP Analysis</h3>
              <p className="text-sm text-gray-600">Get instant CPP calculations and value assessments</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <ChartBarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Smart Insights</h3>
              <p className="text-sm text-gray-600">AI-powered recommendations for better redemptions</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-success-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <LightBulbIcon className="w-6 h-6 text-success-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Pro Tips</h3>
              <p className="text-sm text-gray-600">Learn strategies to maximize your points value</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Chatbot
